import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveWS } from "@/hooks/useLiveWS";
import { toast } from "sonner";
import HowToPlay from "@/components/HowToPlay";
import QuestionExtras from "@/components/QuestionExtras";
import {
  Lightning, CheckCircle, XCircle, Users, WifiSlash, Crown,
  ArrowRight, CaretDown, ClockCounterClockwise,
} from "@phosphor-icons/react";

const API_ROOT = process.env.REACT_APP_BACKEND_URL || "";

const THANKS_SECONDS = 60; // "Thanks for playing" screen lifetime before auto-switch

export default function Daily() {
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [question, setQuestion] = useState(null);
  const [questionStartedAt, setQuestionStartedAt] = useState(null);
  const [picked, setPicked] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [showEnded, setShowEnded] = useState(false);
  const [endedShowId, setEndedShowId] = useState(null);
  const [secLeft, setSecLeft] = useState(0);
  const [scheduledIn, setScheduledIn] = useState(null);
  const [recap, setRecap] = useState(null);
  const [results, setResults] = useState(null);       // ended-show records
  const [lastResults, setLastResults] = useState(null); // most recent show (countdown screen)
  const [recent, setRecent] = useState([]);            // my recent daily games
  const [postSecsLeft, setPostSecsLeft] = useState(null); // thanks-screen countdown
  const [connected, setConnected] = useState(false);

  useLiveWS({
    path: "/api/daily/ws",
    fallbackPath: "/api/daily/live-state",
    onStatus: setConnected,
    onMessage: (m) => {
      if (m.type === "snapshot") {
        setSnap(m.data);
        if (m.data.state === "RUNNING" && m.data.current_question) {
          // Clear local picked/reveal only when the question identity changes,
          // so we don't leak the previous answer's "selected" highlight into the
          // next question (the bug).
          setQuestion((prev) => {
            const next = m.data.current_question;
            if (!prev || prev.qid !== next.qid) {
              setPicked(null);
              setReveal(null);
            }
            return next;
          });
          setQuestionStartedAt(m.data.current_q_started_at);
        } else if (m.data.state === "ENDED") {
          setShowEnded(true);
          setEndedShowId(m.data.show_id);
        }
      } else if (m.type === "show_started") {
        setShowEnded(false); setReveal(null); setQuestion(null); setRecap(null);
        setResults(null); setPostSecsLeft(null); setEndedShowId(null);
      } else if (m.type === "question_started") {
        setQuestion({
          index: m.data.index, qid: m.data.qid, body: m.data.body,
          options: m.data.options, category: m.data.category,
        });
        setQuestionStartedAt(m.data.started_at);
        setReveal(null);
        setPicked(null);
      } else if (m.type === "question_ended") {
        setReveal({ correct_index: m.data.correct_index, winner_username: m.data.winner_username, total_correct: m.data.total_correct });
      } else if (m.type === "show_ended") {
        setShowEnded(true);
        setEndedShowId(m.data.show_id);
        setQuestion(null);
        setReveal(null);
      }
    },
  });

  // Question countdown
  useEffect(() => {
    if (!question || !questionStartedAt) { setSecLeft(0); return; }
    const start = new Date(questionStartedAt).getTime();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      setSecLeft(Math.max(0, 15 - elapsed));
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [question, questionStartedAt]);

  useEffect(() => {
    if (!snap?.scheduled_for || snap.state !== "SCHEDULED") { setScheduledIn(null); return; }
    const t = new Date(snap.scheduled_for).getTime();
    const tick = () => setScheduledIn(Math.max(0, Math.floor((t - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [snap?.scheduled_for, snap?.state]);

  // When the show ends: fetch recap + show records, refresh state, start 60s "thanks" window
  useEffect(() => {
    if (!showEnded || !endedShowId) return;
    api.get(`/daily/my-show/${endedShowId}`).then((r) => setRecap(r.data)).catch(() => {});
    api.get(`/daily/show-results/${endedShowId}`).then((r) => setResults(r.data)).catch(() => {});
    api.get("/daily/state").then((r) => setSnap(r.data.show && { ...r.data.show, next_show_at: r.data.next_show_at, online: r.data.online })).catch(() => {});
    setPostSecsLeft(THANKS_SECONDS);
  }, [showEnded, endedShowId]);

  // Thanks-screen countdown → auto-switch to the next-show screen
  useEffect(() => {
    if (postSecsLeft == null) return;
    if (postSecsLeft <= 0) {
      setShowEnded(false);
      setPostSecsLeft(null);
      return;
    }
    const id = setTimeout(() => setPostSecsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [postSecsLeft]);

  // Countdown-screen extras: last show records + my recent daily games.
  // Also refresh /state when the WS snapshot is a stale ENDED show so the
  // countdown to the next show renders instead of "Starting soon…".
  useEffect(() => {
    if (question || showEnded) return;
    api.get("/daily/last-results").then((r) => setLastResults(r.data)).catch(() => {});
    api.get("/history?mode=daily&limit=5").then((r) => setRecent(r.data.items || [])).catch(() => {});
    api.get("/daily/state").then((r) => {
      setSnap((prev) => {
        if (prev?.state === "RUNNING") return prev; // never clobber a live show
        return r.data.show && { ...r.data.show, next_show_at: r.data.next_show_at, online: r.data.online };
      });
    }).catch(() => {});
  }, [question, showEnded]);

  const pick = async (i) => {
    if (reveal || !question) return;
    // Deselect: tap the already-picked option to clear it (still within time)
    if (picked === i) {
      const prev = picked;
      setPicked(null);
      try {
        await api.post("/daily/unanswer", { show_id: snap.show_id, question_index: question.index });
      } catch {
        // If server rejects (e.g. reveal just happened), restore the visual
        setPicked(prev);
      }
      return;
    }
    setPicked(i);
    try {
      await api.post("/daily/answer", { show_id: snap.show_id, question_index: question.index, option_index: i });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit");
      setPicked(null);
    }
  };

  // ----- Render states -----

  // Show ended → "Thanks for playing" (auto-switches after THANKS_SECONDS)
  if (showEnded) {
    const nextAt = snap?.next_show_at;
    const nextDelta = nextAt ? Math.max(0, Math.floor((new Date(nextAt).getTime() - Date.now()) / 1000)) : null;
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="daily-thanks-screen">
        <Lightning size={28} weight="duotone" className="text-nk-accent" />
        <div>
          <p className="eyebrow">Show ended</p>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight mt-1">Thanks for playing.</h1>
        </div>

        {recap ? (
          <div className="nk-card p-5 sm:p-6 grid grid-cols-3 gap-4 sm:gap-6" data-testid="daily-recap-card">
            <div><p className="text-xs text-nk-mute mb-1">Answered</p><p className="font-display text-2xl sm:text-3xl font-medium">{recap.answered}/15</p></div>
            <div><p className="text-xs text-nk-mute mb-1">Correct</p><p className="font-display text-2xl sm:text-3xl font-medium text-nk-accent">{recap.correct}/15</p></div>
            <div><p className="text-xs text-nk-mute mb-1">Fastest wins</p><p className="font-display text-2xl sm:text-3xl font-medium text-nk-gold">{recap.per_q_wins}</p></div>
            <div className="col-span-3 border-t border-nk-line pt-4">
              <p className="text-xs text-nk-mute mb-1">Season Score earned</p>
              <p className="font-display text-3xl sm:text-4xl font-medium text-nk-accent">+{recap.season_score}</p>
            </div>
          </div>
        ) : (
          <p className="text-nk-mute text-sm">Loading recap…</p>
        )}

        {/* Show records — who was fastest, how many got each one right */}
        {results?.top_players?.length > 0 && <TopPlayers players={results.top_players} myId={user?.user_id} />}
        {results?.results?.length > 0 && <QuestionRecords results={results.results} />}

        {nextDelta != null && nextDelta > 0 && (
          <div className="nk-card p-5 text-center" data-testid="daily-next-show-card">
            <p className="eyebrow mb-2">Next show in</p>
            <p className="font-display text-3xl font-medium text-nk-accent">
              {formatCountdown(nextDelta)}
            </p>
          </div>
        )}

        {postSecsLeft != null && (
          <div className="flex items-center justify-between gap-3 text-xs text-nk-dim" data-testid="daily-thanks-autoswitch">
            <span>Going back to the countdown in {postSecsLeft}s</span>
            <button
              data-testid="daily-thanks-continue"
              onClick={() => { setShowEnded(false); setPostSecsLeft(null); }}
              className="border border-nk-line hover:border-nk-accent px-3 py-1.5 rounded-sm text-nk-mute hover:text-nk-text transition-colors inline-flex items-center gap-1 shrink-0"
            >
              Continue now <ArrowRight size={11} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Live question
  if (question && !reveal) {
    const pct = (secLeft / 15) * 100;
    const urgency = secLeft < 3 ? "urgent" : secLeft < 5 ? "warn" : "normal";
    return (
      <div className="max-w-3xl mx-auto space-y-5">
        {!connected && (
          <div className="flex items-center gap-2 px-3 py-2 bg-nk-bad/10 border border-nk-bad/40 rounded-sm text-xs text-nk-bad"
               data-testid="connection-indicator">
            <WifiSlash size={12} weight="bold" />
            Reconnecting… (your answers will sync as soon as the connection is back)
          </div>
        )}

        {/* Header — question count + online + timer */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium">
            Question {question.index + 1} <span className="text-white/30">/ 15</span>
          </p>
          <div className="flex items-center gap-4 text-sm font-mono">
            <span className="flex items-center gap-1.5 text-white/55"><Users size={13} /> {snap?.online ?? 0}</span>
            <span className={`tabular-nums text-base font-display ${
              urgency === "urgent" ? "text-nk-bad" : urgency === "warn" ? "text-nk-warn" : "text-nk-gold"
            }`} data-testid="daily-timer">
              {Math.ceil(secLeft)}s
            </span>
          </div>
        </div>

        {/* Timer bar */}
        <div className="h-1.5 nk-timer-bar rounded-full overflow-hidden">
          <div className={`h-full nk-timer-fill ${urgency === "urgent" ? "nk-timer-urgent" : urgency === "warn" ? "nk-timer-warn" : ""}`}
               style={{ width: `${pct}%` }} />
        </div>

        {/* Question board */}
        <div className="nk-board rounded-md p-5 sm:p-8">
          <p className="text-[10px] uppercase tracking-[0.16em] text-nk-gold/70 font-mono mb-4">{question.category}</p>
          <p className="font-display text-xl sm:text-3xl font-medium leading-snug text-white mb-7 sm:mb-9">
            {question.body}
          </p>

          <QuestionExtras extras={question.nke_extras} qid={question.qid} apiUrl={API_ROOT} />

          <div className="grid gap-3">
            {question.options.map((opt, i) => {
              const isSelected = picked === i;
              return (
                <button
                  key={i}
                  data-testid={`daily-option-btn-${i}`}
                  onClick={() => pick(i)}
                  className={`nk-option text-left rounded-md px-4 sm:px-5 py-4 sm:py-4 flex items-center gap-3 ${isSelected ? "nk-option-selected" : ""}`}
                  aria-pressed={isSelected}
                >
                  <span className={`font-mono text-xs w-5 ${isSelected ? "text-nk-gold" : "text-white/45"}`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="flex-1 text-sm sm:text-base">{opt}</span>
                  {isSelected && <CheckCircle size={18} weight="fill" className="text-nk-gold" />}
                </button>
              );
            })}
          </div>

          {picked !== null ? (
            <p className="text-xs text-white/55 mt-5 flex items-center gap-1.5" data-testid="daily-pick-hint">
              <CheckCircle size={11} weight="fill" className="text-nk-gold" />
              You picked <span className="text-nk-gold font-mono">{String.fromCharCode(65 + picked)}</span>.
              Tap it again to change before time runs out.
            </p>
          ) : (
            <p className="text-xs text-white/45 mt-5">Pick an answer. You can change it until time runs out.</p>
          )}
        </div>
      </div>
    );
  }

  // Reveal phase
  if (reveal && question) {
    const myCorrect = picked === reveal.correct_index;
    return (
      <div className="max-w-3xl mx-auto space-y-5 animate-fade-in">
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium">
          Q{question.index + 1} <span className="text-white/30">reveal</span>
        </p>
        <div className="nk-board rounded-md p-5 sm:p-8">
          <p className="font-display text-xl sm:text-2xl font-medium leading-snug mb-6 text-white">{question.body}</p>
          <div className="grid gap-3 mb-6">
            {question.options.map((opt, i) => {
              const isCorrect = i === reveal.correct_index;
              const isPick = i === picked;
              const cls = isCorrect ? "nk-option nk-option-correct" :
                          isPick ? "nk-option nk-option-wrong" :
                          "nk-option nk-option-dimmed";
              return (
                <div key={i} className={`${cls} rounded-md px-4 sm:px-5 py-4 flex items-center gap-3`}>
                  <span className="font-mono text-xs w-5">{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1 text-sm sm:text-base">{opt}</span>
                  {isCorrect && <CheckCircle size={18} weight="fill" />}
                  {isPick && !isCorrect && <XCircle size={18} weight="fill" />}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">You</p>
              <p className={`font-display text-xl ${myCorrect ? "text-nk-good" : "text-nk-bad"}`}>
                {myCorrect ? "Correct" : picked === null ? "No answer" : "Wrong"}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Fastest correct</p>
              <p className="font-display text-xl text-nk-gold">{reveal.winner_username || "Nobody"}</p>
              <p className="text-xs text-white/45 mt-1">{reveal.total_correct} got it right</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pre-show (countdown screen) — also shown after the 60s thanks window
  const isLiveNow = snap?.state === "RUNNING";
  return (
    <div className="space-y-7 max-w-2xl">
      <ScheduleAnnouncement />
      <header>
        <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold/80 font-medium mb-2">NK Daily</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
          {isLiveNow ? <>The show is <span className="italic font-light text-nk-gold">live</span>.</>
                     : <>Three shows a day. <span className="italic font-light text-nk-gold">One shot</span> each.</>}
        </h1>
        <p className="text-sm sm:text-base text-white/65 mt-3 max-w-lg leading-relaxed">
          {isLiveNow
            ? "Stay on this page — the next question is on its way."
            : "Fifteen questions. Everyone plays at the same time. The fastest correct answer wins a bonus."}
        </p>
      </header>

      <div
        className="nk-board rounded-md p-6 sm:p-8 text-center"
        data-testid="daily-prestate-card"
      >
        {isLiveNow ? (
          <div data-testid="daily-joinable-now">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-nk-bad font-mono mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-nk-bad animate-pulse-dot" /> LIVE
            </span>
            <p className="font-display text-3xl sm:text-4xl font-medium text-nk-gold">Joinable now</p>
            <p className="text-sm text-white/65 mt-3">The show is running — answers count immediately.</p>
          </div>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 mb-3">Next show in</p>
            {scheduledIn !== null && scheduledIn > 0 ? (
              <p className="font-display text-5xl sm:text-6xl font-medium tracking-tight text-nk-gold tabular-nums">
                {formatCountdown(scheduledIn)}
              </p>
            ) : (
              <p className="font-display text-3xl font-medium text-nk-gold">Starting soon…</p>
            )}
          </>
        )}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-white/55">
          <Users size={12} />
          <span>{snap?.online ?? 0} player{(snap?.online ?? 0) === 1 ? "" : "s"} {isLiveNow ? "playing" : "waiting"}</span>
        </div>
        {user?.is_admin && (
          <div className="mt-8 pt-6 border-t border-white/8">
            <p className="text-[10px] uppercase tracking-wider text-nk-gold mb-2">Admin demo controls</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={async () => {
                  try { await api.post("/daily/admin/start-now"); toast.success("Show starting…"); }
                  catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                }}
                className="nk-btn nk-btn-gold text-xs px-3 py-1.5"
              >Start now</button>
              <button
                onClick={async () => {
                  try { await api.post("/daily/admin/reset-today"); toast.success("Reset"); }
                  catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                }}
                className="nk-btn nk-btn-secondary text-xs px-3 py-1.5"
              >Reset today</button>
            </div>
          </div>
        )}
      </div>

      <HowToPlay
        title="How NK Daily works"
        steps={[
          { title: "Join the live show", desc: "Two shows every day. Everyone answers the same 15 questions at the same time." },
          { title: "You have 15 seconds", desc: "Each question stays open for 15 seconds. Answer before the time runs out." },
          { title: "Fastest correct wins a bonus", desc: "Every correct answer gives you points. Fastest correct on each question wins extra." },
        ]}
        points={[
          { label: "Correct answer", value: "+20 SS" },
          { label: "Fastest correct", value: "+50 SS" },
          { label: "Questions / show", value: "15" },
          { label: "Max / show", value: "1,050 SS" },
        ]}
      />

      {lastResults?.found && <LastShowCard data={lastResults} myId={user?.user_id} />}

      {recent.length > 0 && <RecentDailyGames items={recent} />}
    </div>
  );
}

/* ---------- Records & history sub-components ---------- */

function TopPlayers({ players, myId, compact = false }) {
  const list = compact ? players.slice(0, 3) : players.slice(0, 5);
  return (
    <div className="nk-card p-4 sm:p-5" data-testid="daily-top-players">
      <p className="eyebrow mb-3 flex items-center gap-1.5">
        <Crown size={12} weight="fill" className="text-nk-gold" /> Top players this show
      </p>
      <div className="space-y-1.5">
        {list.map((p) => (
          <div key={p.user_id}
               className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm ${
                 p.user_id === myId ? "bg-nk-accent/10 border border-nk-accent/40" : "border border-nk-line/40"
               }`}>
            <span className="font-mono text-xs text-nk-dim w-5">#{p.rank}</span>
            <span className="flex-1 truncate font-medium">{p.username}{p.user_id === myId && <span className="text-nk-accent text-[10px] ml-1.5">you</span>}</span>
            <span className="font-mono text-xs text-nk-accent shrink-0">{p.correct}/15</span>
            {p.fastest_wins > 0 && (
              <span className="font-mono text-[10px] text-nk-gold shrink-0">{p.fastest_wins}× fastest</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionRecords({ results }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="nk-card overflow-hidden" data-testid="daily-question-records">
      <button onClick={() => setOpen((o) => !o)} data-testid="daily-question-records-toggle"
              className="w-full flex items-center justify-between px-4 sm:px-5 py-3.5 text-left hover:bg-nk-surface/40 transition-colors">
        <span className="text-sm font-medium">Results for each question</span>
        <CaretDown size={13} className={`text-nk-mute transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 space-y-1">
          {results.map((r) => (
            <div key={r.index} className="flex items-center gap-2 sm:gap-3 py-1.5 border-b border-nk-line/30 last:border-0 text-xs">
              <span className="font-mono text-nk-dim w-7 shrink-0">Q{r.index + 1}</span>
              <span className="text-nk-mute shrink-0">{r.correct_count}/{r.answered} correct</span>
              <span className="flex-1 text-right truncate">
                {r.winner_username
                  ? <span className="text-nk-gold">{r.winner_username}{r.winner_ms != null && <span className="text-nk-dim font-mono ml-1.5">{(r.winner_ms / 1000).toFixed(1)}s</span>}</span>
                  : <span className="text-nk-dim">no one got it</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LastShowCard({ data, myId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="nk-card overflow-hidden" data-testid="daily-last-show">
      <button onClick={() => setOpen((o) => !o)} data-testid="daily-last-show-toggle"
              className="w-full flex items-center justify-between px-4 sm:px-5 py-3.5 text-left hover:bg-nk-surface/40 transition-colors">
        <span className="text-sm font-medium flex items-center gap-2">
          <Crown size={14} weight="duotone" className="text-nk-gold" /> Last show results
        </span>
        <CaretDown size={13} className={`text-nk-mute transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-5 space-y-4">
          {data.top_players?.length > 0 ? <TopPlayers players={data.top_players} myId={myId} /> : (
            <p className="text-xs text-nk-mute">No records for the last show yet.</p>
          )}
          {data.results?.length > 0 && <QuestionRecords results={data.results} />}
        </div>
      )}
    </div>
  );
}

function RecentDailyGames({ items }) {
  return (
    <div className="nk-card p-4 sm:p-5" data-testid="daily-recent-games">
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow flex items-center gap-1.5">
          <ClockCounterClockwise size={12} weight="duotone" /> Your recent shows
        </p>
        <Link to="/history?mode=daily" data-testid="daily-history-link"
              className="text-[11px] text-nk-accent inline-flex items-center gap-1 hover:gap-1.5 transition-all">
          Full history <ArrowRight size={10} />
        </Link>
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <div key={it.hist_id} className="flex items-center gap-3 px-3 py-2 border border-nk-line/40 rounded-sm text-xs">
            <span className="flex-1 font-medium">{it.correct}/{it.total || 15} correct</span>
            {it.fastest_wins > 0 && <span className="text-nk-gold font-mono">{it.fastest_wins}× fastest</span>}
            <span className="text-nk-accent font-mono">+{it.ss_earned} SS</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatCountdown(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * ScheduleAnnouncement — dismissible banner announcing the 3-slot NK Daily
 * schedule (12:00 / 19:00 / 22:00 UTC). Dismissal persists in localStorage
 * so we don't re-nag users on every navigation.
 */
const ANNOUNCE_KEY = "nk_daily_3slot_announce_v1";

// Shift a UTC hour by `offset` hours and format as HH:00 (wraps at 24)
const shiftHour = (hour, offset) => `${String((hour + offset + 24) % 24).padStart(2, "0")}:00`;

function ScheduleAnnouncement() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(ANNOUNCE_KEY) === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  const close = () => {
    try { localStorage.setItem(ANNOUNCE_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };
  return (
    <div
      className="nk-card p-4 border-l-2 border-nk-gold/70 flex items-start gap-3"
      data-testid="daily-schedule-announcement"
    >
      <Lightning size={18} weight="fill" className="text-nk-gold mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="eyebrow text-nk-gold mb-1">NK Daily now runs 3 times a day</p>
        <p className="text-sm text-nk-text">
          Catch a show at <span className="text-nk-gold font-medium">12:00</span>,
          {" "}<span className="text-nk-gold font-medium">19:00</span> or
          {" "}<span className="text-nk-gold font-medium">22:00</span> UTC
          {" "}<span className="text-nk-mute">(GMT · same as Accra ·
          {" "}<span className="text-nk-text">{shiftHour(22, 1)}</span> in Lagos)</span>.
          {" "}Same 15-question format · Africa-centred question mix (8 of 15).
        </p>
      </div>
      <button
        onClick={close}
        data-testid="daily-schedule-announcement-close"
        className="text-nk-mute hover:text-nk-text text-xs px-2 py-0.5 shrink-0"
        aria-label="Dismiss announcement"
      >
        Dismiss
      </button>
    </div>
  );
}
